import React from "react";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plane, Check, X } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const statusColor = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

const Holidays = () => {
  const { data, isLoading } = useSWR("/holidays", fetcher);

  const decide = async (id, action) => {
    try {
      await api.post(`/holidays/${id}/${action}`);
      toast.success(`Holiday ${action}d`);
      mutate("/holidays");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Time off</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Holidays</h1>
        <p className="text-sm text-slate-500 mt-1">Approve or reject subcontractor holiday requests.</p>
      </div>

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}

      <div className="space-y-3" data-testid="holidays-list">
        {(data || []).map((h) => (
          <Card key={h.id} className="p-5 rounded-2xl border-brand-border flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`holiday-${h.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-slate-100 grid place-items-center text-slate-600">
                <Plane className="w-4 h-4" />
              </div>
              <div>
                <div className="font-heading font-bold text-brand-text">{h.driver_name}</div>
                <div className="text-[11px] font-mono text-slate-500 mt-0.5">{h.driver_code} · {h.holiday_type}</div>
                <div className="text-sm text-slate-700 mt-1">{ddmmyyyy(h.start_date)} — {ddmmyyyy(h.end_date)}</div>
                {h.reason && <div className="text-xs text-slate-500 mt-1">{h.reason}</div>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={`uppercase tracking-wider text-[10px] ${statusColor[h.status] || ""}`}>{h.status}</Badge>
              {h.status === "pending" && (
                <>
                  <Button size="sm" onClick={() => decide(h.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${h.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => decide(h.id, "reject")} data-testid={`reject-${h.id}`}>
                    <X className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </>
              )}
            </div>
          </Card>
        ))}
        {!isLoading && (data || []).length === 0 && (
          <Card className="p-12 rounded-2xl text-center text-sm text-slate-500">
            <Plane className="w-6 h-6 mx-auto mb-2 opacity-40" />No holiday requests yet.
          </Card>
        )}
      </div>
    </div>
  );
};

export default Holidays;
